/**
 * The permission catalog's copy, in the reader's language.
 *
 * `permissionCatalog` arrives from the BFF with `moduleFa`, `labelFa` and
 * `descriptionFa` — Persian, from `parsina.bff.clinic/src/auth/permissionCatalog.js`.
 * That was the whole story while the panel was Persian-only; with a second
 * language it means the RBAC screen reads Persian in the middle of an English
 * one, and the string audit cannot see it because there is no Persian in this
 * app's source.
 *
 * The catalog already carries a stable `key` and `module`, so the fix is here
 * rather than on the wire: translate from the key, and **fall back to the
 * server's own label** for anything this catalogue has not got.
 *
 * The fallback is not defensive padding. Permission keys are platform-owned
 * (clinic-app/CLAUDE.md §6.3): adding one is a release, and a client deployed
 * against a NEWER BFF will legitimately see a key it has no copy for. Showing
 * the server's Persian is right there — a permission a person cannot read at all
 * is a permission they cannot grant, and «مشاهدهٔ گزارش‌ها» on an English screen
 * is a smaller problem than a blank row in a role editor.
 *
 * When a key IS added: mirror it into `people.permissions.<key>.{label,description}`
 * in every locale. `i18nCoverage.test.js` catches the second half of that (a key
 * in one catalogue and not the other); nothing can catch the first half, because
 * the source of truth is a different repository.
 */

/**
 * The module heading a group of permissions sits under.
 * `people.modules.<module>`, falling back to the server's `moduleFa`.
 */
export const moduleLabel = (perm, t, has) =>
  (has(`people.modules.${perm.module}`)
    ? t(`people.modules.${perm.module}`)
    : perm.moduleFa);

/** A permission's own name. `people.permissions.<key>.label`. */
export const permissionLabel = (perm, t, has) =>
  (has(`people.permissions.${perm.key}.label`)
    ? t(`people.permissions.${perm.key}.label`)
    : perm.labelFa);

/** The line under it. Optional on both sides — a permission may simply have none. */
export const permissionDescription = (perm, t, has) =>
  (has(`people.permissions.${perm.key}.description`)
    ? t(`people.permissions.${perm.key}.description`)
    : perm.descriptionFa);

/** A built-in role template's name. `people.roleTemplates.<key>`. */
export const roleTemplateLabel = (template, t, has) =>
  (has(`people.roleTemplates.${template.key}`)
    ? t(`people.roleTemplates.${template.key}`)
    : template.nameFa);
